import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Icon,
  MenuItem,
  Pagination,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { Error, ExpandMore, Settings } from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { YearGroupPicker } from "../../question/components/SubjectYearGroupPicker";
import { usePagination } from "../../../hooks/usePagination";
import Grid from "@mui/material/Grid";
import GeneralLoading from "../../../shared-components/Loading";
import { AcademicYearSelect } from "../../../shared-components/AcademicYearSelect";

const ConfirmDialog = ({
  schoolClass,
  classToMerge,
  onConfirm,
  open,
  setOpen,
}) => {
  const handleClose = () => {
    setOpen(false);
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>Confirm Merge</DialogTitle>
      <DialogContent>
        <Typography>Are you sure? This can not be undone.</Typography>
        <Typography variant={"body2"}>
          {classToMerge.name} will be removed and its information re-assigned to{" "}
          {schoolClass.name}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant={"contained"}>
          Merge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MergeClassForm = ({
  schoolClass,
  possibleClasses,
  onClose,
  onSuccess,
}) => {
  const [classToMerge, setClassToMerge] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const ApiClient = useApiClient();

  const handleMerge = async () => {
    try {
      const result = await ApiClient.schools.mergeClasses({
        schoolClass: schoolClass.id,
        mergeClass: classToMerge.id,
      });
      if (result?.success) {
        enqueueSnackbar("Class merged", { variant: "success" });
        onSuccess();
        onClose();
      } else {
        enqueueSnackbar(result?.message || "Failed to merge class", {
          variant: "error",
        });
      }
    } catch (e) {
      enqueueSnackbar("Failed to merge class", { variant: "error" });
    }
  };

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        classToMerge={classToMerge}
        schoolClass={schoolClass}
        onConfirm={handleMerge}
      />
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Merge class <strong>{schoolClass.name}</strong>
          </Typography>
          {/* Subject Dropdown */}
          <Autocomplete
            options={possibleClasses}
            getOptionLabel={(option) => option.name || ""}
            value={
              possibleClasses.find((cls) => cls.id === classToMerge.id) || null
            }
            onChange={(_event, newValue) => setClassToMerge(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Possible Classes"
                variant="outlined"
                fullWidth
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          disabled={Object.keys(classToMerge).length === 0}
          onClick={() => setConfirmOpen(true)}
          variant="contained"
        >
          Merge Classes
        </Button>
      </DialogActions>
    </>
  );
};

const ManageClassForm = ({
  schoolClasses,
  onSuccess,
  onClose,
  subjects,
  yearGroups,
  singleClass = false,
}) => {
  if (singleClass) {
    if (schoolClasses.length !== 1) {
      throw new Error(
        "ManageClassForm: singleClass=true requires exactly one school class",
      );
    }
  }

  const [formValues, setFormValues] = useState({
    name: "",
    wondeId: "",
    yearGroupId: "",
    subjectId: "",
  });

  const ApiClient = useApiClient();

  const handleSave = async () => {
    try {
      const result = await ApiClient.schools.updateSchoolClass({
        schoolClasses: schoolClasses.map(
          (schoolClass) => schoolClass?.id || schoolClass,
        ),
        subject: formValues.subjectId,
        yearGroup: formValues.yearGroupId,
        wondeId: singleClass ? formValues.wondeId : null,
        singleClass: singleClass,
      });
      if (result?.success) {
        enqueueSnackbar("Class updated", { variant: "success" });
        onSuccess();
        onClose();
      } else {
        enqueueSnackbar(result?.message || "Failed to update class", {
          variant: "error",
        });
      }
    } catch (e) {
      enqueueSnackbar("Failed to update classs", { variant: "error" });
    }
  };

  useEffect(() => {
    if (singleClass) {
      const schoolClass = schoolClasses[0];
      setFormValues({
        name: schoolClass.name || "",
        wondeId: schoolClass.wondeId || "",
        yearGroupId: schoolClass.yearGroup?.id || "",
        subjectId: schoolClass.subject?.id || "",
      });
    } else {
      setFormValues({
        name: "",
        wondeId: "",
        yearGroupId: schoolClasses[0]?.yearGroup?.id || "",
        subjectId: schoolClasses[0]?.subject?.id || "",
      });
    }
  }, [schoolClasses]);

  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  return (
    <>
      {" "}
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Editing{" "}
            <strong>
              {singleClass
                ? schoolClasses[0].name
                : `${schoolClasses.length} classes`}
            </strong>
          </Typography>

          {/* Editable Wonde ID */}
          {singleClass && (
            <TextField
              name="wondeId"
              label="Wonde ID"
              variant="outlined"
              value={formValues.wondeId}
              onChange={handleChange}
              fullWidth
            />
          )}

          {/* Subject Dropdown */}
          <Autocomplete
            options={subjects}
            getOptionLabel={(option) => option.name || ""}
            value={subjects.find((s) => s.id === formValues.subjectId) || null}
            onChange={(_event, newValue) =>
              setFormValues({
                ...formValues,
                subjectId: newValue ? newValue.id : "",
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subject"
                variant="outlined"
                fullWidth
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {/* Year Group Dropdown */}
          <TextField
            select
            name="yearGroupId"
            label="Year Group"
            variant="outlined"
            value={formValues.yearGroupId}
            onChange={handleChange}
            fullWidth
          >
            {yearGroups.map((yg) => (
              <MenuItem key={yg.id} value={yg.id}>
                {yg.yearGroup}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </>
  );
};
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ mt: 2 }}>{children}</Box>}
    </div>
  );
}

const ManageClassDialog = ({
  open,
  onClose,
  schoolClass,
  yearGroups = [],
  subjects = [],
  possibleClasses = [],
  onSuccess,
}) => {
  const [tab, setTab] = useState(0);

  const hasWondeId = !!schoolClass?.wondeId;
  useEffect(() => {
    if (hasWondeId) {
      setTab(0);
    } else {
      setTab(1);
    }
  }, [hasWondeId]);

  if (!schoolClass) return null;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage School Class</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_e, newValue) => setTab(newValue)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Manage" />
        <Tab label="Merge" disabled={!hasWondeId} />
      </Tabs>
      <TabPanel value={tab} index={0}>
        <ManageClassForm
          singleClass
          schoolClasses={[schoolClass]}
          onSuccess={onSuccess}
          onClose={onClose}
          subjects={subjects}
          yearGroups={yearGroups}
        />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <MergeClassForm
          schoolClass={schoolClass}
          onSuccess={onSuccess}
          onClose={onClose}
          possibleClasses={possibleClasses}
        />
        {/* TODO: add merge content */}
      </TabPanel>
    </Dialog>
  );
};

const ManageMultipleClassesDialog = ({
  open,
  onClose,
  schoolClasses,
  yearGroups = [],
  subjects = [],
  onSuccess,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage School Classes</DialogTitle>
      <ManageClassForm
        schoolClasses={schoolClasses}
        onSuccess={onSuccess}
        onClose={onClose}
        subjects={subjects}
        yearGroups={yearGroups}
      />
    </Dialog>
  );
};

const SchoolClassListItem = ({
  schoolClass,
  onManageClicked,
  isChecked,
  onChecked,
}) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ width: "100%" }}
    >
      <Checkbox
        checked={isChecked}
        onChange={(e) => onChecked(schoolClass, e.target.checked)}
      />

      <Box
        sx={{
          flex: 1,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <Typography>{schoolClass.name}</Typography>

            <Typography variant="body2" color="text.secondary">
              {schoolClass.wondeLastSynced
                ? `Last Synced ${formatDistanceToNow(
                    new Date(schoolClass.wondeLastSynced),
                    {
                      addSuffix: true,
                    },
                  )}`
                : "Not synced with Wonde"}
            </Typography>
          </Box>
          <Chip
            label={schoolClass.isActive ? "Active" : "Not active"}
            color={schoolClass.isActive ? "primary" : "error"}
            size="small"
          />
          <Chip label={schoolClass.subject.name} size="small" />
          <Chip
            label={`Year ${schoolClass.yearGroup.yearGroup}`}
            size="small"
          />
          <Chip label={schoolClass.academicYear.name} size="small" />
        </Stack>

        {/* View Button */}
        <Stack direction="row" spacing={2} alignItems="center">
          {schoolClass.wondeNeedsReview && (
            <>
              <Typography>Requires review</Typography>
              <Icon color={"error"}>
                <Error />
              </Icon>
            </>
          )}
          <Button
            variant="outlined"
            endIcon={<Settings />}
            onClick={() => onManageClicked(schoolClass)}
          >
            Manage
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

const ActiveStates = {
  all: "all",
  active: "active",
  needsReview: "needsReview",
};

export default function SchoolClassManager({ school, subjects, yearGroups }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [multipleDialogOpen, setMultipleDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState({});
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedYearGroup, setSelectedYearGroup] = useState(null);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeStateFilter, setActiveStateFilter] = useState(ActiveStates.all);
  const [debouncedValue, setDebouncedValue] = useState(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const ApiClient = useApiClient();

  const {
    options: schoolClasses,
    page,
    pageInfo,
    handlePageChange,
    loading,
    refetch,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.schools.getFilteredSchoolClassesSuperuser({
        school: school.id,
        searchTerm: debouncedValue,
        yearGroup: selectedYearGroup?.id,
        academicYear: selectedAcademicYear,
        wondeState: activeStateFilter,
        first,
        after,
      }),
    10,
    {
      school,
      debouncedValue,
      selectedYearGroup,
      selectedAcademicYear,
      activeStateFilter,
    },
  );

  const needsReviewCount = useMemo(
    () =>
      schoolClasses.filter((cls) => cls.wondeNeedsReview && cls.isActive)
        .length,
    [schoolClasses],
  );

  const handleManageClass = (schoolClass) => {
    setSelectedClass(schoolClass);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedClass({});
    setOpen(false);
  };
  const handleCloseMultipleDialog = () => {
    setSelectedClasses([]);
    setMultipleDialogOpen(false);
  };

  const handleClassSelected = (schoolClass, checked) => {
    setSelectedClasses((prev) => {
      if (checked) {
        return [...prev, schoolClass.id];
      }

      return prev.filter((id) => id !== schoolClass.id);
    });
  };
  const handleSelectAll = (_, checked) => {
    if (checked) {
      setSelectedClasses((prev) => [
        ...new Set([...prev, ...schoolClasses.map((cls) => cls.id)]),
      ]);
    } else {
      setSelectedClasses((prev) =>
        prev.filter((id) => !schoolClasses.some((cls) => cls.id === id)),
      );
    }
  };

  const allDisplayedSelected =
    schoolClasses.length > 0 &&
    schoolClasses.every((cls) => selectedClasses.includes(cls.id));

  const someDisplayedSelected = schoolClasses.some((cls) =>
    selectedClasses.includes(cls.id),
  );

  return (
    <Box sx={{ mt: 2 }}>
      <ManageClassDialog
        open={open}
        onClose={handleClose}
        schoolClass={selectedClass}
        subjects={subjects}
        yearGroups={yearGroups}
        possibleClasses={schoolClasses.filter(
          (cls) => cls.id !== selectedClass.id,
        )}
        onSuccess={() => refetch()}
      />
      <ManageMultipleClassesDialog
        open={multipleDialogOpen}
        onClose={handleCloseMultipleDialog}
        schoolClasses={selectedClasses}
        subjects={subjects}
        yearGroups={yearGroups}
        onSuccess={() => refetch()}
      />
      <Accordion expanded={expanded} variant={"outlined"}>
        <AccordionSummary onClick={() => setExpanded(!expanded)}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="subtitle2"
                fontWeight="medium"
                sx={{ ml: 1, color: theme.palette.primary.main }}
              >
                School Classes
              </Typography>
              {needsReviewCount > 0 && (
                <Chip
                  label={`${needsReviewCount} Active classes need review`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Stack>
          </Stack>
          <ExpandMore />
        </AccordionSummary>
        <AccordionDetails>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="flex-start"
            spacing={2}
            mb={2}
          >
            <TextField
              label="Search by name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              sx={{ width: { xs: "100%", md: 300 } }}
            />

            <Box sx={{ width: { xs: "100%", md: 200 } }}>
              <YearGroupPicker
                yearGroups={yearGroups}
                selectedYearGroup={selectedYearGroup}
                setSelectedYearGroup={setSelectedYearGroup}
              />
            </Box>

            <AcademicYearSelect
              value={selectedAcademicYear}
              onChange={setSelectedAcademicYear}
              size="small"
              sx={{ width: { xs: "100%", md: 220 } }}
            />

            <ToggleButtonGroup
              value={activeStateFilter}
              exclusive
              onChange={(_, newFilter) => {
                if (newFilter !== null) {
                  setActiveStateFilter(newFilter);
                }
              }}
              size="small"
            >
              <ToggleButton value={ActiveStates.all}>All</ToggleButton>
              <ToggleButton value={ActiveStates.active}>Active</ToggleButton>
              <ToggleButton value={ActiveStates.needsReview}>
                Needs Review
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allDisplayedSelected}
                  indeterminate={someDisplayedSelected && !allDisplayedSelected}
                  onChange={handleSelectAll}
                />
              }
              label="Select all"
            />

            <Button
              variant={"contained"}
              onClick={() => setMultipleDialogOpen(true)}
              disabled={selectedClasses.length === 0}
            >
              Update {selectedClasses.length} classes
            </Button>
          </Stack>
          <Box sx={{ position: "relative" }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  backgroundColor: "rgba(255,255,255,0.4)",
                }}
              >
                <GeneralLoading />
              </Box>
            )}

            {schoolClasses.map((schoolClass) => (
              <SchoolClassListItem
                key={schoolClass.id}
                schoolClass={schoolClass}
                onManageClicked={handleManageClass}
                isChecked={selectedClasses.includes(schoolClass.id)}
                onChecked={handleClassSelected}
              />
            ))}
          </Box>
        </AccordionDetails>
        {page && pageInfo && handlePageChange && (
          <Grid size={12}>
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                page={page}
                onChange={handlePageChange}
                count={pageInfo.hasNextPage ? page + 1 : page}
                color="primary"
              />
            </Box>
          </Grid>
        )}
      </Accordion>
    </Box>
  );
}
